import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  EmailViewContainer,
  AttachmentCard,
  AttachmentsList,
  AttachmentsHeader,
  EmailErrorView,
  FileName,
  FileType,
  FileInfo,
  ModalBody,
  FileIconWrapper,
  EmailCard,
  CardHeader,
  SubjectHeader,
  SubjectTitle,
  MetaSection,
  SenderInfo,
  TimeStamp,
  Icon,
  ToggleButton,
  RecipientSection,
  RecipientRow,
  RecipientLabel,
  RecipientList,
  EmailTag,
  Avatar,
  EmailBody,
  LoadingContainer,
  AttachmentsSection
} from './Styles';
import { Document, Page } from 'react-pdf';
import { truncateText } from "../../../utils/helper";
import Loader from "../../../components/Loader";
import { Modal } from "antd";
import { getEmailDetails } from "../../../utils/services/statusMonitoringAPI";
import { EMAIL_VIEWER } from './TranslationValues';
import { withTranslation } from "react-i18next";
import { ChevronDownIcon, ChevronUpIcon, ClockIcon, PaperClipIcon, PDFIcon, TeamIcon, UserIcon } from "../../../images/svgIcons";
import { useParams } from "react-router-dom";

const namespaces = ['EMAIL_VIEWER'];

const EmailViewer = ({ t }) => {
  const [numPages, setNumPages] = useState(null);
  const { payloadId } = useParams();
  const [emailContent, setEmailContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingHTML, setLoadingHTML] = useState(false);
  const [error, setError] = useState(null);
  const [emailLoadError, setEmailLoadError] = useState(false);
  const [showRecipients, setShowRecipients] = useState(false);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [currentPdfUrl, setCurrentPdfUrl] = useState(null);
  const [emailData, setEmailData] = useState(null);
  
  const iframeRef = useRef(null);

  const payload = useMemo(() => ({
    payloadId,
  }), [payloadId]);

  const fetchEmailDetails = useCallback(async () => {
    try {   
      setLoading(true);
      const res = await getEmailDetails(payload.payloadId);
      if (res && res.statusCode === 5800 && res.data) {
        await setEmailData(res.data);
        if (res.data.htmlFile?.htmlFileStatus?.code === 5802) {
          setEmailContent(res.data.htmlFile?.emailTemplateUrl);
        } else {
          setEmailLoadError(true);
        }
      }
      else {
        setError(true);
      }
    } catch (err) {
      console.error("Failed to load email details: ", err);
    } finally {
      setLoading(false);
    }
  }, [payloadId]);

  useEffect(() => {
    fetchEmailDetails();
  }, [fetchEmailDetails]);

  const loadHTMLIntoIframe = async () => {
    setLoadingHTML(true);
    try {
      const response = await fetch(emailContent);
      const blob = await response.blob();
      if(blob.size === 0) {
        setEmailLoadError(true);
      } else setEmailLoadError(false)
      const htmlText = await blob.text();
      const iframe = iframeRef.current;
      if (!iframe) return;

      const iframeDoc =
        iframe.contentDocument || iframe.contentWindow.document;

      iframeDoc.open();
      iframeDoc.write(htmlText);
      iframeDoc.close();

    } catch (err) {
      console.error("Failed to load HTML into iframe:", err);
    } finally {
      setLoadingHTML(false);
    }
  };

  useEffect(() => {
    if (!emailContent) return;

    const interval = setInterval(() => {
      if (iframeRef.current) {
        clearInterval(interval);
        loadHTMLIntoIframe();
      }
    }, 50);

    return () => clearInterval(interval);
  }, [emailContent]);

  const handleAttachmentClick = (attachment) => {
    if (attachment.link) {
      setCurrentPdfUrl(attachment.link);
      setPdfModalOpen(true);
    }
  };

  const getAvatarColor = (index) => {
    const colors = ['#f56a00', '#7265e6', '#ffbf00', '#00a2ae', '#52c41a'];
    return colors[index % colors.length];
  };

  if (loading) {
    return (
      <EmailViewContainer>
        <Loader />
      </EmailViewContainer>
    );
  }

  if (error) {
    return (
      <EmailViewContainer>
        <EmailCard>
          <EmailErrorView>
            <h3>{t(EMAIL_VIEWER.SOMETHING_WENT_WRONG.key, EMAIL_VIEWER.SOMETHING_WENT_WRONG.value)}</h3>
            <h5>
              {t(EMAIL_VIEWER.LOAD_EMAIL_FAILED.key, EMAIL_VIEWER.LOAD_EMAIL_FAILED.value)}
            </h5>
          </EmailErrorView>
        </EmailCard>
      </EmailViewContainer>
    );
  }

  return (
    <EmailViewContainer>
      <EmailCard>
        <CardHeader>
          <SubjectHeader>
            <SubjectTitle>{truncateText(emailData?.subject, 100)}</SubjectTitle>
          </SubjectHeader>

          <MetaSection>
            <SenderInfo>{emailData?.fromAddress || ''}</SenderInfo>
            <TimeStamp>
              <Icon><ClockIcon /></Icon>
              {new Date().toLocaleString('en-US', {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </TimeStamp>
          </MetaSection>

          <ToggleButton onClick={() => setShowRecipients(!showRecipients)}>
            <span>
              {showRecipients
                ? t(EMAIL_VIEWER.HIDE_RECIPIENT_DETAILS.key, EMAIL_VIEWER.HIDE_RECIPIENT_DETAILS.value)
                : t(EMAIL_VIEWER.SHOW_RECIPIENT_DETAILS.key, EMAIL_VIEWER.SHOW_RECIPIENT_DETAILS.value)}
            </span>
            <Icon>{showRecipients ? <ChevronUpIcon /> : <ChevronDownIcon />}</Icon>
          </ToggleButton>
        </CardHeader>

        {emailData && showRecipients && (
          <RecipientSection>
            <RecipientRow>
              <RecipientLabel>
                <Icon><UserIcon /></Icon>
                {t(EMAIL_VIEWER.TO_LABEL.key, EMAIL_VIEWER.TO_LABEL.value)}
              </RecipientLabel>
              <RecipientList>
                {emailData.to?.map((toAddress, index) => (
                  (toAddress.trim().length > 0 && <EmailTag color="green" key={index} >
                    <Avatar size="small" bg={getAvatarColor(3)}>
                      {(toAddress.trim().charAt(0).toUpperCase())}
                    </Avatar>
                    {toAddress}
                  </EmailTag>)
                ))}
              </RecipientList>
            </RecipientRow>

            {emailData.cc && emailData.cc.length > 0 && (
              <RecipientRow>
                <RecipientLabel>
                  <Icon><TeamIcon /></Icon>
                  {t(EMAIL_VIEWER.CC_LABEL.key, EMAIL_VIEWER.CC_LABEL.value)}
                </RecipientLabel>
                <RecipientList>
                  {emailData.cc?.map((email, index) => (
                    (email.trim().length > 0 && <EmailTag key={index} color="orange">
                      <Avatar size="small" bg={getAvatarColor(index + 10)}>
                        {(email.trim().charAt(0).toUpperCase())}
                      </Avatar>
                      {email}
                    </EmailTag>)
                  ))}
                </RecipientList>
              </RecipientRow>
            )}
          </RecipientSection>
        )}
        <div style={{display: 'flex', height: '100%', overflow: 'auto'}}>
          <EmailBody>
            {loadingHTML ? (
              <LoadingContainer>
                <Loader />
                <div style={{ color: '#8c8c8c' }}>
                  {t(
                    EMAIL_VIEWER.LOADING_EMAIL_CONTENT.key,
                    EMAIL_VIEWER.LOADING_EMAIL_CONTENT.value
                  )}
                </div>
              </LoadingContainer>
            ) : emailLoadError ? (
              <p style={{ color: '#8c8c8c', padding: '20px', textAlign: 'center' }}>
                {t(
                  EMAIL_VIEWER.EMAIL_NOT_FOUND.key,
                  EMAIL_VIEWER.EMAIL_NOT_FOUND.value
                )}
              </p>
            ) : null}
              <iframe ref={iframeRef} title="Email Content" style={{display : (loadingHTML || emailLoadError) ? 'none' : 'block'}} />
          </EmailBody>

          {emailData && emailData.attachments && emailData.attachments.length > 0 && (
            <AttachmentsSection>
              <AttachmentsHeader>
                <Icon size="18px"><PaperClipIcon /></Icon>
                {emailData.attachments.length} {emailData.attachments.length === 1
                  ? t(EMAIL_VIEWER.ATTACHMENT.key, EMAIL_VIEWER.ATTACHMENT.value)
                  : t(EMAIL_VIEWER.ATTACHMENTS.key, EMAIL_VIEWER.ATTACHMENTS.value)}
              </AttachmentsHeader>
              <AttachmentsList>
                {emailData.attachments.map((attachment, index) => (
                  <AttachmentCard 
                    key={index} 
                    onClick={() => attachment.link ? handleAttachmentClick(attachment) : null}
                    disabled={attachment.attachmnetStatus?.code!==5803}
                  >
                    <FileIconWrapper disabled={attachment.attachmnetStatus?.code!==5803}>
                      <PDFIcon />
                    </FileIconWrapper>
                    <FileInfo>
                      <FileName>{truncateText(attachment.fileName, 30)}</FileName>
                      <FileType>
                        {attachment.attachmnetStatus?.code === 5803
                          ? 'PDF'
                          : t(EMAIL_VIEWER.PDF_IS_DELETED.key, EMAIL_VIEWER.PDF_IS_DELETED.value)}
                      </FileType>
                    </FileInfo>
                  </AttachmentCard>
                ))}
              </AttachmentsList>
            </AttachmentsSection>
          )}
        </div>
        <Modal
          centered
          visible={pdfModalOpen}
          footer={null}
          width="70vw"
          maskClosable={false}
          style={{ minWidth: '768px'}}
          onCancel={() => setPdfModalOpen(false)}
        >
          <ModalBody style={{ paddingTop: '10px'}}>
            {currentPdfUrl && (
              <Document
                file={currentPdfUrl}
                loading={
                  <div style={{ padding: '40px', textAlign: 'center' }}>
                    <div style={{ marginTop: '16px', color: '#8c8c8c' }}>
                      {t(EMAIL_VIEWER.LOADING_PDF.key, EMAIL_VIEWER.LOADING_PDF.value)}
                    </div>
                  </div>
                }
                error={
                  <div>
                    <p style={{ paddingTop: '50px', textAlign: 'center', fontSize: '24px', fontWeight: '600' }}>{t(EMAIL_VIEWER.SOMETHING_WENT_WRONG.key, EMAIL_VIEWER.SOMETHING_WENT_WRONG.value)}</p>
                    <p style={{ padding: '40px', textAlign: 'center', fontSize: '16px' }}>{t(EMAIL_VIEWER.LOAD_PDF_FAILED.key, EMAIL_VIEWER.LOAD_PDF_FAILED.value)}</p>
                  </div>
                }
                onLoadSuccess={({ numPages }) => setNumPages(numPages)}
              >
                {numPages &&
                  Array.from({ length: numPages }, (el, i) => (
                    <Page
                      key={`page_${i + 1}`}
                      pageNumber={i + 1}
                      scale={1.2}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      width={Math.min(window.innerWidth * 0.8, 800)}
                    />
                  ))}
              </Document>
            )}
          </ModalBody>
        </Modal>
      </EmailCard>
    </EmailViewContainer>
  );
};

export default withTranslation(namespaces)(EmailViewer);
